/**
 * Email Templates View
 * Platform-tenant template library (schema is tenant-aware for future tenant use).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Icons } from '../common/Icons';
import { emailTemplatesService } from '../../services/api';
import ChangeNoticeSendPanel from './ChangeNoticeSendPanel';

const EMPTY_FORM = {
  name: '',
  subject: '',
  body_text: '',
  purpose: 'signup_outreach',
  is_active: true,
};

const PURPOSE_LABELS = {
  signup_outreach: 'Signup outreach',
  general: 'General',
  change_management: 'Change management',
};

const EmailTemplatesView = () => {
  const [templates, setTemplates] = useState([]);
  const [mergeFields, setMergeFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [isNew, setIsNew] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const listRes = await emailTemplatesService.getAll();
      setTemplates(listRes.data || []);
    } catch (err) {
      console.error('Failed to load email templates:', err);
      setError('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    emailTemplatesService.getMergeFields(form.purpose).then((fieldsRes) => {
      if (!cancelled) setMergeFields(fieldsRes.data || []);
    }).catch((err) => {
      console.error('Failed to load merge fields:', err);
    });
    return () => {
      cancelled = true;
    };
  }, [form.purpose]);

  const selectTemplate = (tpl) => {
    setIsNew(false);
    setSelectedId(tpl.id);
    setForm({
      name: tpl.name || '',
      subject: tpl.subject || '',
      body_text: tpl.body_text || '',
      purpose: tpl.purpose || 'general',
      is_active: tpl.is_active !== false,
    });
  };

  const startNew = () => {
    setIsNew(true);
    setSelectedId(null);
    setForm({ ...EMPTY_FORM });
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      if (isNew) {
        const created = await emailTemplatesService.create(form);
        await load();
        if (created.data?.id) {
          selectTemplate(created.data);
        }
      } else if (selectedId) {
        await emailTemplatesService.update(selectedId, form);
        await load();
      }
    } catch (err) {
      console.error('Failed to save template:', err);
      setError(err.response?.data?.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedId || !window.confirm('Delete this email template?')) return;
    try {
      setSaving(true);
      await emailTemplatesService.remove(selectedId);
      setSelectedId(null);
      setIsNew(false);
      setForm(EMPTY_FORM);
      await load();
    } catch (err) {
      console.error('Failed to delete template:', err);
      setError(err.response?.data?.message || 'Failed to delete template');
    } finally {
      setSaving(false);
    }
  };

  const insertToken = (key) => {
    const token = `{{${key}}}`;
    setForm((prev) => ({
      ...prev,
      body_text: `${prev.body_text || ''}${prev.body_text ? '' : ''}${token}`,
    }));
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <p style={{ color: 'var(--text-muted)', margin: 0 }}>
          Platform template library for Signup Request outreach and change-management notices.
          Audience (workspaces and roles) is chosen when you send, not on the template.
        </p>
        <button type="button" className="btn btn-primary btn-sm" onClick={startNew}>
          <Icons.Plus size={16} />
          New template
        </button>
      </div>

      {error && (
        <div className="alert alert-warning mb-3">
          <Icons.AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '24px' }}>
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {loading ? (
            <div className="empty-state" style={{ padding: '32px' }}>
              <Icons.Loader size={32} className="spin" />
            </div>
          ) : templates.length === 0 ? (
            <div className="empty-state" style={{ padding: '32px' }}>
              <p>No templates yet</p>
            </div>
          ) : (
            templates.map((tpl) => (
              <button
                key={tpl.id}
                type="button"
                onClick={() => selectTemplate(tpl)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '14px 16px',
                  border: 'none',
                  borderBottom: '1px solid var(--border-color)',
                  background: selectedId === tpl.id ? 'var(--bg-secondary)' : 'transparent',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontWeight: 600 }}>{tpl.name}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                  {PURPOSE_LABELS[tpl.purpose] || tpl.purpose}{tpl.is_active ? '' : ' · inactive'}
                </div>
              </button>
            ))
          )}
        </div>

        <div className="card">
          {!isNew && !selectedId ? (
            <div className="empty-state">
              <Icons.FileText size={40} />
              <p>Select a template or create a new one</p>
            </div>
          ) : (
            <>
              <div className="form-group">
                <label className="form-label">Name</label>
                <input
                  className="form-control"
                  value={form.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Conference thank-you"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Purpose</label>
                  <select
                    className="form-control"
                    value={form.purpose}
                    onChange={(e) => handleChange('purpose', e.target.value)}
                  >
                    <option value="signup_outreach">Signup outreach</option>
                    <option value="general">General</option>
                    <option value="change_management">Change management</option>
                  </select>
                  {form.purpose === 'change_management' && (
                    <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '6px 0 0' }}>
                      Hidden from Signup Request outreach. Audience is chosen when you send, not on this template.
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Active</label>
                  <select
                    className="form-control"
                    value={form.is_active ? 'true' : 'false'}
                    onChange={(e) => handleChange('is_active', e.target.value === 'true')}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Subject</label>
                <input
                  className="form-control"
                  value={form.subject}
                  onChange={(e) => handleChange('subject', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Body</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                  {mergeFields.map((f) => (
                    <button
                      key={f.key}
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => insertToken(f.key)}
                    >
                      {`{{${f.key}}}`}
                    </button>
                  ))}
                </div>
                <textarea
                  className="form-control"
                  rows={14}
                  value={form.body_text}
                  onChange={(e) => handleChange('body_text', e.target.value)}
                />
              </div>

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={saving || !form.name.trim() || !form.subject.trim() || !form.body_text.trim()}
                >
                  {saving ? 'Saving...' : isNew ? 'Create template' : 'Save template'}
                </button>
                {!isNew && selectedId && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleDelete}
                    disabled={saving}
                  >
                    <Icons.Trash size={16} />
                    Delete
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {!isNew && selectedId && form.purpose === 'change_management' && (
        templates.find((t) => t.id === selectedId)?.purpose === 'change_management' ? (
          <ChangeNoticeSendPanel
            templateId={selectedId}
            disabled={saving || form.is_active === false}
          />
        ) : (
          <div className="card" style={{ marginTop: '24px' }}>
            <p style={{ margin: 0, color: 'var(--text-muted)' }}>
              Save this template as Change management to preview recipients and send.
            </p>
          </div>
        )
      )}

      <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default EmailTemplatesView;
